import { useLazyQuery, useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import styled from '@emotion/styled'
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Container as MuiContainer,
  CssBaseline,
  Link,
  Paper as MuiPaper,
  Typography,
  TypographyOwnProps,
} from '@mui/material'
import { spacing } from '@mui/system'
import React, { useEffect, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import CenteredBox from '@/components/CenteredBox'
import Footer from '@/components/Footer'
import GlobalStyle from '@/components/GlobalStyle'
import Navbar from '@/components/navbar/Navbar'
import dashboardItems from '@/components/sidebar/dashboardItems'
import Sidebar from '@/components/sidebar/Sidebar'
import {
  GET_DISPLAY,
  GET_STORE,
  GetDisplayQuery,
  GetDisplayQueryVariables,
  GetStoreQuery,
  GetStoreQueryVariables,
} from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'
import { StoreHelpers } from '@/utils/store.helpers'

const drawerWidth = 258

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`

const Drawer = styled.div`
  ${props => props.theme.breakpoints.up('md')} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 0;
  ${props => props.theme.breakpoints.up('md')} {
    max-width: calc(100% - ${drawerWidth}px);
  }
`

const Paper = styled(MuiPaper)(spacing)

const Container = styled(MuiContainer)`
  height: 100%;
`

const MainContent = styled(Paper)`
  flex: 1;
  background: ${props => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`
interface DashboardType {
  children?: React.ReactNode
}

const StoreNameBreadcrumb: React.FC<
  {
    id: string
  } & TypographyOwnProps
> = props => {
  const { id, ...rest } = props
  const { data, loading } = useQuery<GetStoreQuery, GetStoreQueryVariables>(
    GET_STORE,
    {
      variables: { id },
    },
  )

  return (
    <Typography {...rest}>
      {loading ? (
        <CircularProgress size={15} />
      ) : (
        data?.store.location?.name || ''
      )}
    </Typography>
  )
}

const DisplayNameBreadcrumb: React.FC<
  {
    id: string
  } & TypographyOwnProps
> = props => {
  const { id, ...rest } = props
  const { data, loading } = useQuery<GetDisplayQuery, GetDisplayQueryVariables>(
    GET_DISPLAY,
    {
      variables: { id },
    },
  )

  return (
    <Typography {...rest}>
      {loading ? <CircularProgress size={15} /> : data?.display.name || ''}
    </Typography>
  )
}

const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const router = useLocation()
  const { id, actionGeneratorId, displayId, snapshotId, restockGroupId } =
    useParams()
  const { user } = useLoggedInUser('cache-and-network')
  const hasMultipleStores = user?.hasMultipleStores
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false)
  }, [router.pathname])

  const { t, T } = useT()

  const getBreadcrumbs = () => {
    const path = router.pathname.split('/').filter(Boolean)

    if (['context-images', 'image-feedback'].includes(path[path.length - 1]))
      return null
    const containsStore = path.includes('stores')
    const containsPlanogram = path.includes('planograms')
    const containsDisplays = path.includes('displays')
    const containsEmptyFacing = path.includes('empty-facings')
    const containsCompliance = path.includes('compliance')
    const containsProducts = path.includes('products')
    const startsWithTask = path.length > 0 && path[0] === 'tasks'

    const singleStore = !hasMultipleStores

    const filteredPath =
      singleStore && containsStore
        ? path.filter(segment => segment !== 'stores')
        : path

    const breadcrumbs = filteredPath.map((_, index) => {
      const toSegments =
        singleStore && containsStore
          ? ['stores', ...filteredPath.slice(0, index + 1)]
          : filteredPath.slice(0, index + 1)
      const to = `/${toSegments.join('/')}`

      const currentPath = filteredPath[index]
      let label: string | null = null
      const isLast = index === filteredPath.length - 1
      const onlyOne = filteredPath.length === 1

      const props: TypographyOwnProps = isLast
        ? {
            color: 'textPrimary',
            variant: 'h3',
            sx: { textTransform: 'capitalize' },
          }
        : {
            variant: 'h6',
            // @ts-expect-error need to figure out the types here
            component: Link,
            color: 'inherit',
            underline: 'hover',
            sx: { cursor: 'pointer', textTransform: 'capitalize' },
            onClick: () => handleNavTo(to, { resetParams: false }),
          }
      if (startsWithTask && filteredPath[index] === 'group') {
        return (
          <Typography key={to} {...props} onClick={undefined}>
            Group
          </Typography>
        )
      }
      if (isLast && containsProducts && !onlyOne) {
        label = currentPath
      }
      if (containsEmptyFacing && snapshotId === filteredPath[index]) {
        label = t('common.imageViewer')
      }
      if (containsCompliance && snapshotId === filteredPath[index]) {
        label = t('common.snapshot')
      }
      if (startsWithTask && id === filteredPath[index]) {
        label = t('common.progress')
      }
      if (actionGeneratorId === filteredPath[index]) {
        label = t('common.scheduledRestock')
      }
      if (restockGroupId === filteredPath[index]) {
        label = t('common.restock')
      }
      if (containsStore && id === filteredPath[index]) {
        return <StoreNameBreadcrumb key={to} id={id} {...props} />
      }
      if (containsDisplays && filteredPath[index] === 'displays') {
        return null
      }
      if (containsDisplays && displayId === filteredPath[index]) {
        return <DisplayNameBreadcrumb key={to} id={displayId} {...props} />
      }
      if (containsPlanogram && displayId === filteredPath[index]) {
        return <DisplayNameBreadcrumb key={to} id={displayId} {...props} />
      }
      return (
        <Typography key={to} {...props}>
          {label ||
            t('breadcrumbs.path', { path: currentPath.replace('-', '_') })}
        </Typography>
      )
    })
    return breadcrumbs
  }

  const pathname = window.location.pathname

  const [getStore, storeData] = useLazyQuery<
    GetStoreQuery,
    GetStoreQueryVariables
  >(GET_STORE)

  useEffect(() => {
    if (id && pathname.includes('stores')) {
      getStore({ variables: { id } })
    }
  }, [id, pathname, getStore])

  const Body = pathname.includes('stores') ? (
    storeData.loading ? (
      <CenteredBox>
        <CircularProgress />
      </CenteredBox>
    ) : storeData.error ? (
      <CenteredBox>
        <Alert severity="error">{storeData.error.message}</Alert>
      </CenteredBox>
    ) : id &&
      id === storeData.data?.store.id &&
      pathname.includes('stores') &&
      StoreHelpers.isStoreInactive(storeData.data.store) ? (
      <CenteredBox>
        <Alert severity="error">
          <T
            keyName="store.inactive.reactivateMessage"
            params={{
              t: content => (
                <Link display="inline" onClick={() => handleNavTo('/stores')}>
                  {content}
                </Link>
              ),
            }}
          />
        </Alert>
      </CenteredBox>
    ) : (
      <Outlet />
    )
  ) : (
    <Outlet />
  )

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={dashboardItems(
              t,
              hasMultipleStores,
              user?.activeSubscription ?? undefined,
            )}
          />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            items={dashboardItems(
              t,
              hasMultipleStores,
              user?.activeSubscription ?? undefined,
            )}
          />
        </Box>
      </Drawer>
      <AppContent>
        <Navbar
          onDrawerToggle={handleDrawerToggle}
          breadcrumbs={getBreadcrumbs()}
        />
        <MainContent>
          <Container maxWidth="xl" sx={{ pb: 2 }}>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Breadcrumbs>{getBreadcrumbs()}</Breadcrumbs>
            </Box>
            {children}
            {Body}
          </Container>
        </MainContent>
        <Footer />
      </AppContent>
    </Root>
  )
}

export default Dashboard
